import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { getAvailableOriginalsBySeries, getMeta } from "../data/site";
import { useCurrency } from "../contexts/CurrencyContext";
import Testimonials from "../components/Testimonials";

export default function Originals() {
  const groups = getAvailableOriginalsBySeries();
  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div data-testid="originals-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">Available Now · {total} original paintings</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Originals.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
          Every one-of-one painting currently on the studio wall — signed,
          with a certificate of authenticity, shipped or hand-delivered from
          Denver. Secure checkout via Square; payment plans available.
        </p>
        <nav
          className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          aria-label="Jump to series"
          data-testid="originals-series-nav"
        >
          {groups.map((g) => (
            <a
              key={g.slug}
              href={`#${g.slug}`}
              className="label link-underline text-[hsl(var(--ink-soft))] hover:text-foreground"
              data-testid={`originals-jump-${g.slug}`}
            >
              {g.label} · {g.items.length}
            </a>
          ))}
        </nav>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32 space-y-20 md:space-y-28">
        {groups.map((g) => (
          <section key={g.slug} id={g.slug} data-testid={`originals-section-${g.slug}`} className="scroll-mt-28">
            <div className="flex items-end justify-between gap-6 mb-8 border-b border-border pb-4">
              <div>
                <p className="label mb-2">Series · {g.items.length} available</p>
                <h2 className="serif text-3xl md:text-5xl tracking-tight font-light">
                  {g.label}
                </h2>
              </div>
              <Link
                to={`/work/${g.slug}`}
                className="inline-flex items-center gap-2 label link-underline whitespace-nowrap"
                data-testid={`originals-explore-${g.slug}`}
              >
                Full series <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div
              className={
                g.slug === "the-posse"
                  ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8"
                  : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10"
              }
            >
              {g.items.map((a) => (
                <OriginalCard key={a.id} slug={g.slug} a={a} />
              ))}
            </div>
          </section>
        ))}

        <Testimonials className="!border-t-0 pt-4" eyebrow="What collectors say" />

        <section
          className="border-t border-border pt-16 md:pt-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
          data-testid="originals-commission-cta"
        >
          <div className="md:col-span-7">
            <p className="label mb-3">Don't see the one?</p>
            <h2 className="serif text-3xl md:text-5xl tracking-tight font-light leading-[1.05]">
              Commission a piece{" "}
              <span className="italic text-[hsl(var(--ink-soft))]">
                painted for your wall.
              </span>
            </h2>
            <p className="mt-4 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed">
              Portraits, Black Western scenes, and large-scale statement work
              — scoped to your space, your palette, and your story. Typical
              turnaround is six to ten weeks.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right flex flex-col md:items-end gap-3">
            <Link
              to="/commissions"
              className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
              data-testid="originals-commission-link"
            >
              Start a commission
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/studio-visit"
              className="label link-underline text-[hsl(var(--ink-soft))]"
              data-testid="originals-studio-visit-link"
            >
              Or book a studio visit →
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function OriginalCard({ slug, a }) {
  const meta = getMeta(a.id);
  const { format: fmt } = useCurrency();
  const detail = `/work/${slug}/${a.id}`;
  return (
    <article className="art-card group" data-testid={`original-${a.id}`}>
      <Link to={detail} className="block" data-testid={`original-image-${a.id}`}>
        <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
          <img src={a.image} alt={a.title} className="art-img w-full h-full object-cover" />
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            <span className={`inline-block px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em] ${meta.status === "on-loan" ? "bg-foreground text-background" : "bg-[hsl(var(--accent))] text-background"}`}>
              {meta.status === "on-loan" ? "On Loan · Reserve" : "Available"}
            </span>
            {meta.prints_coming_soon && (
              <span className="inline-block bg-background text-foreground border border-foreground px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
                Prints Coming Soon
              </span>
            )}
          </div>
        </div>
      </Link>
      <div className="mt-4">
        <div className="flex items-baseline justify-between gap-4">
          <Link to={detail} className="serif italic text-xl md:text-2xl hover:underline underline-offset-4">
            {a.title}
          </Link>
          <span className="label">{a.year}</span>
        </div>
        <p className="label mt-1">
          {a.medium} · {a.size}
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="serif text-xl" data-testid={`original-price-${a.id}`}>
            {meta.price ? fmt(meta.price) : "Price on request"}
          </span>
          {meta.buy_url && meta.status === "available" ? (
            <a
              href={meta.buy_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
              data-testid={`original-buy-${a.id}`}
            >
              Buy Now
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          ) : (
            <Link
              to={`${detail}#inquire`}
              className="inline-flex items-center gap-2 border border-foreground px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
              data-testid={`original-inquire-${a.id}`}
            >
              {meta.status === "on-loan" ? "Reserve" : "Inquire"}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
